import { McButton, McInputDate, McInputTime } from '@maersk-global/mds-react-wrapper';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { getDateFromDateTime, getDateTimeFromDate, getDateTimeFromTime, getTimeFromDateTime } from './events/consts';

export type TimeSlot = { from: string; to: string };

const TimeGroup = ({
  isAllDay,
  slots,
  setSlots,
  setError,
  date,
  dateTo,
  type,
}: {
  isAllDay: boolean;
  slots: TimeSlot[];
  setSlots: Dispatch<SetStateAction<TimeSlot[]>>;
  setError: Dispatch<SetStateAction<boolean>>;
  date: string;
  dateTo?: string;
  type?: 'holiday' | 'event';
}) => {
  const [localSlots, setLocalSlots] = useState<TimeSlot[]>(
    slots.length ? [...slots] : [{ from: `${date}T`, to: `${dateTo ? dateTo : date}T` }],
  );
  const [errors, setErrors] = useState<boolean[]>([]);

  const isTimeOnly = useMemo(() => type === 'event' && !isAllDay, [type, isAllDay]);

  useEffect(() => {
    if (isAllDay) {
      setLocalSlots([{ from: `${date}T00:00`, to: `${dateTo ? dateTo : date}T23:59` }]);
      setSlots([]);
      setErrors([]);
    } else if (!isAllDay && !slots.length) {
      const d = [{ from: `${date}T`, to: `${dateTo ? dateTo : date}T` }];
      setLocalSlots(d);
      validateSlots(d);
    }
  }, [isAllDay]);

  const handleChange = (newSlots: TimeSlot[]) => {
    setLocalSlots(newSlots);
    setSlots(newSlots);
    validateSlots(newSlots);
  };

  const validateSlots = (slots: TimeSlot[]) => {
    const errors = slots.map((slot) => slot.to <= slot.from);
    setErrors(errors);
    setError(Boolean(errors.filter((x) => x).length));
  };

  return (
    <div className="HolidaysDrawer__periods__timeslots">
      {isTimeOnly ? <div className="HolidaysDrawer__periods__timeslots__title">Unavailable every day</div> : null}
      {localSlots.map((slot, index) => (
        <div className="HolidaysDrawer__periods__line" key={index}>
          {!isTimeOnly && (
            <McInputDate
              disabled={isAllDay}
              fit="small"
              label="Unavailable from"
              invalid={errors[index]}
              errormessage="Check the range"
              value={getDateFromDateTime(slot.from)}
              input={(e) => {
                const list = [...localSlots];
                // @ts-ignore
                list[index] = { from: getDateTimeFromDate(slot.from, e.target.value), to: slot.to };
                handleChange(list);
              }}
            />
          )}
          <McInputTime
            disabled={isAllDay}
            fit="small"
            label={isTimeOnly ? 'From' : '\u00A0'}
            placeholder="--:--"
            value={getTimeFromDateTime(slot.from)}
            invalid={errors[index]}
            input={(e) => {
              const list = [...localSlots];
              // @ts-ignore
              list[index] = { from: getDateTimeFromTime(slot.from, e.target.value), to: slot.to };
              handleChange(list);
            }}
          />
          <div />
          {!isTimeOnly && (
            <McInputDate
              disabled={isAllDay}
              fit="small"
              label="To"
              invalid={errors[index]}
              value={getDateFromDateTime(slot.to)}
              input={(e) => {
                const list = [...localSlots];
                // @ts-ignore
                list[index] = { from: slot.from, to: getDateTimeFromDate(slot.to, e.target.value) };
                handleChange(list);
              }}
            />
          )}
          <McInputTime
            disabled={isAllDay}
            fit="small"
            label={isTimeOnly ? 'To' : '\u00A0'}
            placeholder="--:--"
            value={getTimeFromDateTime(slot.to)}
            invalid={errors[index]}
            input={(e) => {
              const list = [...localSlots];
              // @ts-ignore
              list[index] = { from: slot.from, to: getDateTimeFromTime(slot.to, e.target.value) };
              handleChange(list);
            }}
          />
          {index > 0 ? (
            <McButton
              className="HolidaysDrawer__periods__line__button"
              icon="trash"
              variant="outlined"
              appearance="neutral"
              hiddenlabel
              fit="small"
              click={() => {
                const a = [...localSlots];
                a.splice(index, 1);
                handleChange(a);
              }}
            />
          ) : null}
          {!isAllDay && index === localSlots.length - 1 && localSlots.length < 5 ? (
            <McButton
              className="HolidaysDrawer__periods__line__button"
              icon="plus"
              variant="outlined"
              hiddenlabel
              fit="small"
              click={() => setLocalSlots((c) => [...c, { from: `${date}T`, to: `${dateTo ? dateTo : date}T` }])}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default TimeGroup;
